import React from 'react';
import FacilityActions from '../actions/FacilityActions';
import ViewerActions from '../actions/ViewerActions';
import AssetSetsActions from '../actions/AssetSetsActions';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import {
    canOpenKnowledgeViewEditPanel,
    FACILITY_SLIDEOUT_PANEL_IDS,
} from '../helpers/facilitySlideoutPanelSession';
import { getAssignedSchemaIds, orderAssignedSchemas } from '../helpers/assignedAssetSetTaxonomies';
import { getApplicableFacilityAssetSets } from '../helpers/facilityAssetSetOntologyFilter';
import type { AssignedAssetSetTaxonomy } from './AssignedAssetSetClassificationTree';
import SchemaServices from '../services/SchemaServices';
import ComponentHelpers from '../helpers/ComponentHelpers';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import ViewerStore from '../stores/ViewerStore';
import AssetSetVisualizationStore from '../stores/AssetSetVisualizationStore';
import UrlUtilities from '@shared/common/UrlUtilities';
import FacilitySlideoutSessionPanel from './FacilitySlideoutSessionPanel';
import {
    SessionPanelMeasuredContent,
    SessionPanelToolbar,
    type SessionPanelRenderContext,
} from '../../shared/panels/SessionPanel';
import AssetSetDisplayPriorityEditor, {
    createMemoryDisplayColorRepository,
} from './AssetSetDisplayPriorityEditor';
import { mergeViewerAssetSets } from '../helpers/viewerAssetSetModel';
import { getPanoCompatibleAssetSets, getPanoInvalidAssetSetIDs, is3DFallbackAssetSet } from '../helpers/assetSetVisualizationModel';
import { toDisplayConfiguration, toKnowledgeViewAssetSets } from '../helpers/knowledgeViewVisualizationModel';

const KNOWLEDGE_VIEW_EDIT_PANEL_ID = FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit;

interface KnowledgeViewEditState {
    isOpen: boolean;
    isSidebarOpen: boolean;
    selectedFacility: IFacilityDetail | null;
    selectedOntology: IOntologyPart | null;
    knowledgeBook: IKnowledgeBook | null;
    knowledgeViews: IKnowledgeView[];
    knowledgeBookAssetSets: IAssetSet[];
    selectedKnowledgeView: IKnowledgeViewDetail | null;
}

const getPanelState = (): KnowledgeViewEditState => ({
    isOpen: FacilityStore.getKnowledgeViewEditBarOpen(),
    isSidebarOpen: FacilityStore.getSidebarOpen(),
    selectedFacility: FacilityStore.getSelectedFacility(),
    selectedOntology: FacilityStore.getSelectedOntology(),
    knowledgeBook: KnowledgeBookStore.getSelectedKnowledgeBook(),
    knowledgeViews: KnowledgeBookStore.getAllKnowledgeView(),
    knowledgeBookAssetSets: KnowledgeBookStore.getAllAssetSet(),
    selectedKnowledgeView: ViewerStore.getSelectedKnowledgeView(),
});

const displayColorRepository = createMemoryDisplayColorRepository();

const KnowledgeViewEdit = () => {
    const [panelState, setPanelState] = React.useState<KnowledgeViewEditState>(getPanelState);
    const [isChangingKnowledgeView, setIsChangingKnowledgeView] = React.useState(false);
    const isSaving = ViewerStore.isKnowledgeViewEditBusy();
    const [facilityAssetSets, setFacilityAssetSets] = React.useState<IAssetSet[]>([]);
    const [assignedTaxonomies, setAssignedTaxonomies] = React.useState<AssignedAssetSetTaxonomy[]>([]);
    const [facilityDataLoaded, setFacilityDataLoaded] = React.useState(false);
    const [workspaceHeight, setWorkspaceHeight] = React.useState(0);
    const [workspaceElement, setWorkspaceElement] = React.useState<HTMLDivElement | null>(null);
    const isMountedRef = React.useRef(true);
    const facilityAssetSetsLoadGenerationRef = React.useRef(0);

    const loadFacilityAssetSets = React.useCallback(async (
        facilityID: string | undefined,
        clearOnError: boolean,
    ) => {
        const generation = ++facilityAssetSetsLoadGenerationRef.current;
        if (!facilityID) {
            if (isMountedRef.current) setFacilityAssetSets([]);
            return;
        }

        try {
            const loadedAssetSets = (await AssetSetsActions.refreshFacilityAssetSets(facilityID, false))
                .filter(assetSet => !assetSet.KnowledgeBookID);
            if (!isMountedRef.current || generation !== facilityAssetSetsLoadGenerationRef.current) return;
            setFacilityAssetSets(loadedAssetSets);
        } catch (error) {
            if (!isMountedRef.current || generation !== facilityAssetSetsLoadGenerationRef.current) return;
            if (clearOnError) setFacilityAssetSets([]);
            console.error('Unable to load Facility Asset Sets for Knowledge View Edit.', error);
        }
    }, []);

    React.useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            facilityAssetSetsLoadGenerationRef.current++;
        };
    }, []);

    React.useEffect(() => {
        const loadState = () => setPanelState(getPanelState());

        FacilityStore.addChangeListener(loadState);
        KnowledgeBookStore.addChangeListener(loadState);
        ViewerStore.addChangeListener(loadState);

        return () => {
            FacilityStore.removeChangeListener(loadState);
            KnowledgeBookStore.removeChangeListener(loadState);
            ViewerStore.removeChangeListener(loadState);
        };
    }, []);

    React.useEffect(() => {
        const facility = panelState.selectedFacility;
        if (!facility) {
            facilityAssetSetsLoadGenerationRef.current++;
            setFacilityAssetSets([]);
            setAssignedTaxonomies([]);
            setFacilityDataLoaded(false);
            return;
        }

        let cancelled = false;
        setFacilityDataLoaded(false);
        const load = async () => {
            try {
                await loadFacilityAssetSets(facility.ID, true);
                let schemas = FacilityStore.getSchemas() ?? [];
                const schemaIDs = getAssignedSchemaIds(facility);
                if (schemaIDs.some(id => !schemas.some(schema => schema.ID === id))) {
                    schemas = await SchemaServices.getAll();
                }
                const loadedTaxonomies = await Promise.all(
                    orderAssignedSchemas(facility, schemas).map(async schema => ({
                        schema,
                        taxonomy: await SchemaServices.getAssetSetTaxonomyDetail(schema.ID),
                    }))
                );
                if (cancelled) return;
                setAssignedTaxonomies(loadedTaxonomies);
            } catch {
                if (cancelled) return;
                setAssignedTaxonomies([]);
            } finally {
                if (!cancelled) setFacilityDataLoaded(true);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [panelState.selectedFacility?.ID, panelState.selectedFacility?.SchemaID,
        (panelState.selectedFacility?.Schemas ?? []).join('|'), loadFacilityAssetSets]);

    React.useEffect(() => {
        const facilityID = panelState.selectedFacility?.ID;
        if (!facilityID) return;

        const handleFacilityAssetSetsChanged = (changedFacilityID: string) => {
            if (changedFacilityID === facilityID) {
                void loadFacilityAssetSets(facilityID, false);
            }
        };

        AssetSetVisualizationStore.addAssetSetsChangeListener(handleFacilityAssetSetsChanged);
        return () => AssetSetVisualizationStore.removeAssetSetsChangeListener(handleFacilityAssetSetsChanged);
    }, [panelState.selectedFacility?.ID, loadFacilityAssetSets]);

    React.useLayoutEffect(() => {
        const workspace = workspaceElement;
        if (!workspace || typeof ResizeObserver === 'undefined') {
            setWorkspaceHeight(workspace?.clientHeight ?? 0);
            return;
        }

        const updateHeight = () => setWorkspaceHeight(workspace.clientHeight);
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(workspace);
        return () => observer.disconnect();
    }, [workspaceElement, panelState.isOpen, panelState.selectedKnowledgeView?.ID]);

    const finishReturnToKnowledgeViews = React.useCallback(() => {
        FacilityActions.closeKnowledgeViewEditBar();
        FacilityActions.openKBbar();
    }, []);

    const returnToKnowledgeViews = React.useCallback(() => {
        ViewerActions.checkForKnowledgeViewChanges(finishReturnToKnowledgeViews);
    }, [finishReturnToKnowledgeViews]);

    const handlePanelOpenChange = React.useCallback((open: boolean) => {
        if (!open) {
            FacilityActions.closeKnowledgeViewEditBar();
            return;
        }

        const selectedKnowledgeBook = KnowledgeBookStore.getSelectedKnowledgeBook();
        const selectedKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        const hasResolvedKnowledgeBookPermission = ComponentHelpers.hasFacilityManagePermission() ||
            !!FacilityStore.getVisibleTaxonomyClassificationDetail();
        if (!hasResolvedKnowledgeBookPermission || canOpenKnowledgeViewEditPanel(
            ComponentHelpers.hasModifyKnowledgeBookPermission(selectedKnowledgeBook),
            selectedKnowledgeBook?.ID,
            selectedKnowledgeView?.ID
        )) {
            FacilityActions.openKnowledgeViewEditBar();
            return;
        }

        FacilityActions.closeKnowledgeViewEditBar();
        if (selectedKnowledgeBook?.ID && selectedKnowledgeView?.ID) {
            FacilityActions.openKBbar();
        }
    }, []);

    const handleKnowledgeViewChange = React.useCallback(async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const knowledgeViewId = event.target.value;
        const currentKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (!knowledgeViewId || knowledgeViewId === currentKnowledgeView?.ID) {
            return;
        }
        const knowledgeView = KnowledgeBookStore.getAllKnowledgeView().find(view => view.ID === knowledgeViewId);
        if (!knowledgeView) return;
        setIsChangingKnowledgeView(true);
        try {
            await FacilityActions.openKnowledgeViewInViewer(knowledgeView);
        } finally {
            setIsChangingKnowledgeView(false);
        }
    }, []);

    const { isOpen, isSidebarOpen, knowledgeBook, knowledgeViews, knowledgeBookAssetSets, selectedKnowledgeView } = panelState;
    const hasRequiredSelection = !!knowledgeBook?.ID && !!selectedKnowledgeView?.ID;
    const hasResolvedKnowledgeBookPermission = ComponentHelpers.hasFacilityManagePermission() ||
        !!FacilityStore.getVisibleTaxonomyClassificationDetail();
    const canEditDisplayConfiguration = ComponentHelpers.hasModifyKnowledgeBookPermission(knowledgeBook);
    const canOpenEditPanel = ViewerStore.isEditingKnowledgeView() && (!hasResolvedKnowledgeBookPermission
        ? hasRequiredSelection
        : canOpenKnowledgeViewEditPanel(
            canEditDisplayConfiguration,
            knowledgeBook?.ID,
            selectedKnowledgeView?.ID
        ));
    const openKnowledgeViewForm = React.useCallback(() => {
        if (!selectedKnowledgeView || isChangingKnowledgeView || isSaving || !canEditDisplayConfiguration) {
            return;
        }
        KnowledgeBookActions.beginEditKnowledgeView(selectedKnowledgeView);
    }, [canEditDisplayConfiguration, isChangingKnowledgeView, isSaving, selectedKnowledgeView]);
    const attachedClassName = isOpen && canOpenEditPanel
        ? (isSidebarOpen ? 'show-extend' : 'show')
        : '';

    React.useEffect(() => {
        if (!isOpen || !hasRequiredSelection || canOpenEditPanel) return;

        FacilityActions.closeKnowledgeViewEditBar();
        FacilityActions.openKBbar();
    }, [canOpenEditPanel, hasRequiredSelection, isOpen]);

    const allAssetSets = React.useMemo(
        () => mergeViewerAssetSets(knowledgeBookAssetSets, facilityAssetSets),
        [facilityAssetSets, knowledgeBookAssetSets]
    );
    const isPanoKnowledgeView = selectedKnowledgeView?.Type === 1;
    const invalidAssetSetIDs = React.useMemo(
        () => isPanoKnowledgeView ? getPanoInvalidAssetSetIDs(allAssetSets) : undefined,
        [isPanoKnowledgeView, allAssetSets]
    );
    const editableKnowledgeBookAssetSets = React.useMemo(
        () => isPanoKnowledgeView
            ? getPanoCompatibleAssetSets(knowledgeBookAssetSets)
            : knowledgeBookAssetSets,
        [isPanoKnowledgeView, knowledgeBookAssetSets]
    );
    const treeAssetSets = React.useMemo(
        () => isPanoKnowledgeView
            ? getPanoCompatibleAssetSets(facilityAssetSets)
            : getApplicableFacilityAssetSets(
                facilityAssetSets,
                panelState.selectedOntology?.ID ?? selectedKnowledgeView?.OntologyID,
                { includeEmpty: true }
            ),
        [facilityAssetSets, isPanoKnowledgeView, panelState.selectedOntology?.ID, selectedKnowledgeView?.OntologyID]
    );
    const displayConfiguration = React.useMemo(
        () => toDisplayConfiguration(selectedKnowledgeView, allAssetSets),
        [allAssetSets, selectedKnowledgeView]
    );

    const updateDisplayConfiguration = React.useCallback((configuration: IAssetSetDisplayConfiguration) => {
        const currentKnowledgeView = ViewerStore.getSelectedKnowledgeView();
        if (!currentKnowledgeView || !canEditDisplayConfiguration || !ViewerActions.canAuthorKnowledgeView()) return;
        ViewerActions.setSelectedKnowledgeViewData({
            ...currentKnowledgeView,
            AssetSets: toKnowledgeViewAssetSets(configuration, currentKnowledgeView.Type === 1),
        });
    }, [canEditDisplayConfiguration]);


    return (
        <FacilitySlideoutSessionPanel
            attachedClassName={attachedClassName}
            className="kb-bar-3d knowledge-view-edit-bar"
            elementId="knowledge-view-edit-content"
            isOpen={isOpen && canOpenEditPanel}
            keepMounted={true}
            onOpenChange={handlePanelOpenChange}
            panelId={KNOWLEDGE_VIEW_EDIT_PANEL_ID}
            pinnedWidth={360}
        >
            {(panelContext: SessionPanelRenderContext) => (
                <>
                    <SessionPanelToolbar
                        context={panelContext}
                        itemClassName="kb-tool-bar-item enabled-btn"
                        listClassName="kb-tool-bar"
                    >
                        <li
                            aria-label={InovxLoader.t('facility:action.backToKnowledgeView')}
                            className="knowledge-view-edit-back-button"
                            onClick={returnToKnowledgeViews}
                            role="button"
                            title={InovxLoader.t('facility:action.backToKnowledgeView')}
                        />
                        <li className="kb-title">{InovxLoader.t('facility:action.editView')}</li>
                    </SessionPanelToolbar>

                    <SessionPanelMeasuredContent bottomPadding={10} fallbackOffset={52} minHeight={0}>
                        {measuredHeight => (
                            <div className="knowledge-view-edit-content" style={{ height: measuredHeight }}>
                                <div className="manage-asset-sets-knowledge-book-row knowledge-view-edit-book-row">
                                    <img
                                        alt=""
                                        aria-hidden="true"
                                        className="manage-asset-sets-knowledge-book-icon"
                                        src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/KBLarge.png`}
                                    />
                                    <span className="display-name-text" title={knowledgeBook?.Name}>
                                        {knowledgeBook?.Name}
                                    </span>
                                </div>

                                <label className="knowledge-view-edit-label" htmlFor="knowledge-view-edit-select">
                                    {InovxLoader.t('facility:knowledgeBook.knowledgeView')}:
                                </label>
                                <div className="knowledge-view-edit-selector-row">
                                    <select
                                        aria-label={InovxLoader.t('facility:knowledgeBook.knowledgeView')}
                                        className="kb-select knowledge-view-edit-select"
                                        disabled={isChangingKnowledgeView || isSaving}
                                        id="knowledge-view-edit-select"
                                        onChange={handleKnowledgeViewChange}
                                        value={selectedKnowledgeView?.ID ?? ''}
                                    >
                                        {knowledgeViews.map(knowledgeView => (
                                            <option key={knowledgeView.ID} value={knowledgeView.ID}>
                                                {knowledgeView.Name}
                                            </option>
                                        ))}
                                    </select>
                                    {canEditDisplayConfiguration && (
                                        <button
                                            aria-label={InovxLoader.t('facility:action.editView')}
                                            className="knowledge-view-edit-name-button"
                                            disabled={!selectedKnowledgeView?.ID || isChangingKnowledgeView || isSaving}
                                            onClick={openKnowledgeViewForm}
                                            title={InovxLoader.t('facility:action.editView')}
                                            type="button"
                                        >
                                            <img
                                                alt=""
                                                aria-hidden="true"
                                                src={`${UrlUtilities.getBaseApplicationUrl()}images/KB/Pen.png`}
                                            />
                                        </button>
                                    )}
                                </div>

                                <div className="knowledge-view-edit-workspace kb-asset-sets-layout" ref={setWorkspaceElement}>
                                    {!facilityDataLoaded && (
                                        <div className="asset-set-display-message">
                                            {InovxLoader.t('facility:assetSetVisualization.loading')}
                                        </div>
                                    )}
                                    {facilityDataLoaded && displayConfiguration && workspaceHeight > 0 && (
                                        <AssetSetDisplayPriorityEditor
                                            key={`${knowledgeBook?.ID}-${selectedKnowledgeView?.ID}`}
                                            configuration={displayConfiguration}
                                            onChange={updateDisplayConfiguration}
                                            assetSets={isPanoKnowledgeView ? allAssetSets.filter(set => !is3DFallbackAssetSet(set)) : allAssetSets}
                                            invalidAssetSetIDs={invalidAssetSetIDs}
                                            treeAssetSets={treeAssetSets}
                                            assignedTaxonomies={assignedTaxonomies}
                                            canEdit={canEditDisplayConfiguration && !isSaving}
                                            showRemoveAllDisplayPriorityAction={true}
                                            height={workspaceHeight}
                                            includeKnowledgeBookRoot={true}
                                            selectedKnowledgeBook={knowledgeBook}
                                            knowledgeBookAssetSets={editableKnowledgeBookAssetSets}
                                            colorRepository={displayColorRepository}
                                        />
                                    )}
                                </div>


                            </div>
                        )}
                    </SessionPanelMeasuredContent>
                </>
            )}
        </FacilitySlideoutSessionPanel>
    );
};

export default KnowledgeViewEdit;
